import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  Copy,
  ExternalLink,
  ArrowLeft,
  Loader2,
  QrCode,
  Info,
  Pin,
  Landmark,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, getFileUrl } from "@/lib/api";

/**
 * Bank Details page — 3-tab layout redesigned Feb 2026 to match the user's
 * shared HTML mockup:
 *   • Cash Deposit — Google Sheet link (kept clickable inside a yellow
 *     "pin" note card, with a big CTA button to open it).
 *   • Bank Transfer — Vertically listed account cards with HOLDER /
 *     ACCOUNT NO. / IFSC / BANK / BRANCH / UPI ID rows + copy buttons.
 *   • QR Code — Grid of "who + QR" tiles, one per enabled QR code.
 *     `label_custom` is treated as the person / label the QR belongs
 *     to; `label_type` renders as a small mono chip below.
 */
export default function BankDetails() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedQr, setExpandedQr] = useState(null);
  const [tab, setTab] = useState("cash");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data: fresh } = await api.get("/bank-details");
      setData(fresh);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener("bank_details.updated", onRefresh);
    return () => window.removeEventListener("bank_details.updated", onRefresh);
  }, []);

  const copy = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied ✓`);
    } catch {
      toast.error("Copy blocked");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#385C4B]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-4">
        <div className="bg-white border border-[#C8553D]/30 rounded-xl p-6 max-w-md">
          <div className="text-[#C8553D] text-sm font-semibold">Failed to load</div>
          <div className="text-xs text-[#6B6B61] mt-2">{error}</div>
        </div>
      </div>
    );
  }
  if (!data?.tab_enabled) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-4">
        <div className="bg-white border border-[#DCDCD3] rounded-xl p-8 max-w-md text-center">
          <Info className="w-8 h-8 text-[#6B6B61] mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-sm text-[#1C1C19] font-semibold">
            Bank Details are currently hidden
          </div>
          <div className="text-xs text-[#6B6B61] mt-1">
            Ask your admin to enable this tab.
          </div>
        </div>
      </div>
    );
  }

  const accounts = data.accounts || [];
  const qrs = data.qr_codes || [];

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <div className="bg-white border-b border-[#DCDCD3] sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to="/chat"
            data-testid="bank-back-btn"
            className="p-1.5 rounded-full hover:bg-[#F5F5F0] text-[#6B6B61]"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={2} />
          </Link>
          <div className="flex items-center gap-2 flex-1">
            <div className="w-9 h-9 rounded-lg bg-[#385C4B] flex items-center justify-center">
              <Home className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <div>
              <div
                className="text-sm font-semibold text-[#1C1C19]"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Bank Account Details
              </div>
              {data.updated_by_name && (
                <div className="text-[10px] text-[#6B6B61] font-mono">
                  Updated by {data.updated_by_name}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {/* Tabs */}
        <div
          className="bg-white border border-[#DCDCD3] rounded-xl p-1 flex gap-1 mb-4"
          data-testid="bank-tabs"
        >
          <TabBtn active={tab === "cash"} onClick={() => setTab("cash")} testid="bank-tab-cash">
            Cash Deposit
          </TabBtn>
          <TabBtn active={tab === "bank"} onClick={() => setTab("bank")} testid="bank-tab-bank">
            Bank Transfer
          </TabBtn>
          <TabBtn active={tab === "qr"} onClick={() => setTab("qr")} testid="bank-tab-qr">
            QR Code
          </TabBtn>
        </div>

        {/* Tab: Cash Deposit */}
        {tab === "cash" && (
          <div data-testid="bank-panel-cash" className="space-y-4">
            <SectionTitle>Cash Deposit</SectionTitle>
            <div
              className="bg-[#FFF7E6] border border-[#E0A020]/40 rounded-xl p-4"
              data-testid="bank-cash-note"
            >
              <div className="flex items-start gap-2 mb-2">
                <Pin
                  className="w-4 h-4 text-[#B8791F] flex-shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <div className="text-sm font-semibold text-[#1C1C19] leading-relaxed">
                  अकाउंट डिटेल्स लिंक (केवल कैश डिपॉज़िट के लिए):
                </div>
              </div>
              {data.google_sheet_url ? (
                <a
                  href={data.google_sheet_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="bank-cash-sheet-link"
                  className="text-xs text-[#385C4B] underline underline-offset-2 hover:text-[#2D4A3C] font-mono break-all block mb-2"
                >
                  {shortLink(data.google_sheet_url)}
                </a>
              ) : (
                <div className="text-xs text-[#6B6B61] italic mb-2">
                  Admin ne abhi Sheet link add nahi kiya.
                </div>
              )}
              <div className="text-xs text-[#8A6046] leading-relaxed">
                कृपया हर दिन लिंक चेक करके ही डिपॉज़िट करें।
              </div>
            </div>

            {data.google_sheet_enabled && data.google_sheet_url && (
              <a
                href={data.google_sheet_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bank-cash-cta"
                className="bg-gradient-to-r from-[#385C4B] to-[#2D4A3C] text-white rounded-xl p-4 flex items-center gap-3 hover:from-[#2D4A3C] hover:to-[#1E3529] transition-all shadow-sm"
              >
                <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
                  <ExternalLink className="w-5 h-5" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <div
                    className="text-sm font-semibold"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    Check Live Account Sheet
                  </div>
                  <div className="text-[11px] opacity-80 mt-0.5">
                    Open the daily-updated Google Sheet
                  </div>
                </div>
              </a>
            )}

            {/* Free-form admin notice at bottom for extra context */}
            {data.important_notice && (
              <div
                className="bg-white border border-[#DCDCD3] rounded-xl p-4"
                data-testid="bank-important-notice"
              >
                <div className="text-[11px] uppercase tracking-widest text-[#385C4B] font-semibold mb-2">
                  Notes from Admin
                </div>
                <div className="text-xs text-[#1C1C19] whitespace-pre-wrap leading-relaxed">
                  {renderWithLinks(data.important_notice)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab: Bank Transfer */}
        {tab === "bank" && (
          <div data-testid="bank-panel-bank" className="space-y-4">
            <SectionTitle>Bank Account Transfer</SectionTitle>
            {accounts.length === 0 ? (
              <div
                className="bg-white border border-dashed border-[#DCDCD3] rounded-xl p-8 text-center"
                data-testid="bank-no-accounts"
              >
                <Landmark
                  className="w-8 h-8 text-[#DCDCD3] mx-auto mb-2"
                  strokeWidth={1.25}
                />
                <div className="text-sm text-[#6B6B61]">
                  No active bank accounts right now.
                </div>
              </div>
            ) : (
              accounts.map((acc) => <AccountCard key={acc.account_id} acc={acc} onCopy={copy} />)
            )}
          </div>
        )}

        {/* Tab: QR Code */}
        {tab === "qr" && (
          <div data-testid="bank-panel-qr" className="space-y-4">
            <div className="bg-white border border-[#DCDCD3] rounded-xl overflow-hidden">
              <div className="bg-[#385C4B]/5 border-b border-[#DCDCD3] px-4 py-3 flex items-center gap-2">
                <QrCode className="w-4 h-4 text-[#385C4B]" strokeWidth={2} />
                <div
                  className="text-sm font-semibold text-[#1C1C19]"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  Scan any QR to pay ({qrs.length})
                </div>
              </div>
              {qrs.length === 0 ? (
                <div className="p-8 text-center text-xs text-[#6B6B61]" data-testid="bank-no-qrs">
                  Admin ne abhi koi QR enable nahi kiya.
                </div>
              ) : (
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {qrs.map((qr) => (
                    <QRTile
                      key={qr.qr_id}
                      qr={qr}
                      onExpand={() => setExpandedQr(getFileUrl(qr.file_id))}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* QR lightbox */}
      {expandedQr && (
        <div
          className="fixed inset-0 z-[200] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setExpandedQr(null)}
          data-testid="bank-qr-lightbox"
        >
          <img
            src={expandedQr}
            alt="QR code full"
            className="max-w-full max-h-full object-contain bg-white rounded-lg"
          />
        </div>
      )}
    </div>
  );
}

// ---------- helpers ----------

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 text-xs md:text-sm font-semibold py-2.5 px-3 rounded-lg transition-all ${
        active
          ? "bg-[#385C4B] text-white shadow-sm"
          : "text-[#6B6B61] hover:text-[#385C4B] hover:bg-[#F5F5F0]"
      }`}
      style={{ fontFamily: "Outfit, sans-serif" }}
    >
      {children}
    </button>
  );
}

function SectionTitle({ children }) {
  return (
    <div
      className="text-sm font-semibold text-[#1C1C19]"
      style={{ fontFamily: "Outfit, sans-serif" }}
    >
      {children}
    </div>
  );
}

function AccountCard({ acc, onCopy }) {
  return (
    <div
      className="bg-white border border-[#DCDCD3] rounded-xl overflow-hidden"
      data-testid={`bank-account-${acc.account_id}`}
    >
      <div className="border-b border-[#DCDCD3] px-4 py-3 flex items-center gap-2">
        <div className="w-8 h-8 rounded-md bg-[#385C4B] flex items-center justify-center flex-shrink-0">
          <Landmark className="w-4 h-4 text-white" strokeWidth={2} />
        </div>
        <div
          className="flex-1 text-sm font-semibold text-[#1C1C19] truncate"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {acc.label}
        </div>
        <span className="text-[10px] font-mono uppercase text-[#385C4B] bg-[#385C4B]/10 px-2 py-0.5 rounded-full tracking-wider">
          Active
        </span>
      </div>
      <div className="p-4 space-y-2.5">
        <AccountRow label="Holder" value={acc.account_holder_name} onCopy={onCopy} />
        <AccountRow label="Account No." value={acc.account_number} mono copy onCopy={onCopy} />
        <AccountRow label="IFSC" value={acc.ifsc} mono copy onCopy={onCopy} />
        <AccountRow label="Bank" value={acc.bank_name} />
        {acc.branch && <AccountRow label="Branch" value={acc.branch} />}
        {acc.upi_id && (
          <AccountRow label="UPI ID" value={acc.upi_id} mono copy onCopy={onCopy} />
        )}
      </div>
    </div>
  );
}

function AccountRow({ label, value, mono, copy, onCopy }) {
  return (
    <div
      className="flex items-center gap-3 text-xs"
      data-testid={`bank-row-${label.toLowerCase().replace(/\s|\./g, "-")}`}
    >
      <div className="text-[10px] uppercase tracking-widest text-[#6B6B61] w-24 flex-shrink-0">
        {label}
      </div>
      <div className={`text-[#1C1C19] flex-1 truncate ${mono ? "font-mono" : ""}`}>{value}</div>
      {copy && value && (
        <button
          type="button"
          onClick={() => onCopy(value, label)}
          className="p-1.5 rounded-md hover:bg-[#F5F5F0] text-[#385C4B]"
          title={`Copy ${label}`}
        >
          <Copy className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

// Person-attached QR tile — the "who" chip up top, QR image below.
function QRTile({ qr, onExpand }) {
  const who = qrPersonLabel(qr);
  const kind = qrKindLabel(qr);
  const initial = (who[0] || "?").toUpperCase();
  return (
    <div
      className="border border-[#DCDCD3] rounded-xl p-3 bg-white flex flex-col items-center gap-2"
      data-testid={`bank-qr-tile-${qr.qr_id}`}
    >
      <div className="flex items-center gap-2 w-full">
        <div className="w-6 h-6 rounded-full bg-[#385C4B] text-white text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
          {initial}
        </div>
        <div className="text-xs font-semibold text-[#1C1C19] flex-1 truncate">{who}</div>
      </div>
      <button
        type="button"
        onClick={onExpand}
        className="w-full aspect-square rounded-lg bg-[#F5F5F0] border border-[#DCDCD3] overflow-hidden flex items-center justify-center hover:border-[#385C4B]/50 transition-all"
      >
        <img
          src={getFileUrl(qr.file_id)}
          alt=""
          loading="lazy"
          className="w-full h-full object-contain bg-white"
        />
      </button>
      <div className="text-[10px] font-mono uppercase text-[#385C4B] bg-[#385C4B]/10 px-2 py-0.5 rounded-full tracking-wider">
        {kind}
      </div>
    </div>
  );
}

// ---------- label helpers ----------

const QR_LABEL_DISPLAY = {
  phonepe: "PhonePe",
  paytm: "Paytm",
  gpay: "Google Pay",
  bhim: "BHIM",
  bank: "Bank UPI",
  whatsapp: "WhatsApp Pay",
  other: "QR",
};

// The "who" line — for named QRs (label_custom set), use that; otherwise
// fall back to the payment app name so the tile is never blank.
function qrPersonLabel(qr) {
  const custom = (qr.label_custom || "").trim();
  if (custom) return custom;
  return QR_LABEL_DISPLAY[qr.label_type] || "QR";
}

// The small badge below the QR — payment app kind. Suppressed when the
// admin picked "Other" (they intended the custom label to be the whole
// story, so the badge would just repeat noise).
function qrKindLabel(qr) {
  if (qr.label_type === "other") return "Custom QR";
  return QR_LABEL_DISPLAY[qr.label_type] || qr.label_type.toUpperCase();
}

function shortLink(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    const path = u.pathname.length > 30 ? u.pathname.slice(0, 30) + "…" : u.pathname;
    return `${u.hostname}${path}`;
  } catch {
    return url;
  }
}

function renderWithLinks(text) {
  if (!text) return null;
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((chunk, i) => {
    if (/^https?:\/\//.test(chunk)) {
      return (
        <a
          key={i}
          href={chunk}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#385C4B] underline hover:text-[#2D4A3C] break-all"
          data-testid="bank-notice-link"
        >
          {chunk}
        </a>
      );
    }
    return chunk;
  });
}
